"use client";

import { useState } from "react";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { useRouter } from "next/navigation";
interface DeleteAccountSectionProps {
  labels: Record<string, string>;
  basePath: string;
}
export default function DeleteAccountSection({
  labels,
  basePath
}: DeleteAccountSectionProps) {
  const sdk = useFansUnitedSDK();
  const {
    signOut
  } = useAuth();
  const router = useRouter();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
    setConfirmationText("");
    setError(null);
  };
  const handleConfirmDelete = async () => {
    if (confirmationText.toLowerCase() !== "delete") {
      setError(labels.deleteConfirmationError || 'Please type "delete" to confirm');
      return;
    }
    if (!sdk) {
      setError("SDK not available");
      return;
    }
    setIsDeleting(true);
    setError(null);
    try {
      await sdk.profile.getOwn().delete();
      await signOut();
      router.push(basePath || "/");
    } catch (err) {
      setError(err instanceof Error ? err.message : labels.deleteError || "Failed to delete account");
      setIsDeleting(false);
    }
  };
  const handleCancel = () => {
    setShowConfirmDialog(false);
    setConfirmationText("");
    setError(null);
  };
  const isConfirmationValid = confirmationText.toLowerCase() === "delete";
  return <>
            <div className="bg-error/5 border border-error/30 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 text-error">
                    <Trash2 className="h-5 w-5" />
                    <h3 className="text-lg font-semibold">{labels.dangerZone || "Danger Zone"}</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                    {labels.dangerZoneDescription || "Irreversible and destructive actions"}
                </p>

                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-foreground">{labels.deleteAccount || "Delete Account"}</h4>
                    <p className="text-sm text-muted-foreground">
                        {labels.deleteAccountDescription || "Permanently delete your account and all associated data. This action cannot be undone."}
                    </p>
                </div>

                <button onClick={handleDeleteClick} disabled={!sdk} className="py-2 px-4 bg-error text-white rounded-lg font-medium text-sm hover:bg-error/90 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    {labels.deleteAccount || "Delete Account"}
                </button>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50" onClick={handleCancel} />

                    {/* Dialog */}
                    <div className="relative bg-background border-2 border-error/50 rounded-lg p-6 max-w-lg w-full mx-4 space-y-4">
                        {/* Header */}
                        <div className="text-center space-y-2">
                            <div className="flex justify-center">
                                <div className="p-2 rounded-full bg-error/10">
                                    <AlertTriangle className="w-6 h-6 text-error" />
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold text-error">
                                {labels.confirmDeleteTitle || "Delete Account?"}
                            </h3>
                            <p className="text-muted-foreground">
                                {labels.deleteConfirmation || "This will permanently delete your account. This action cannot be undone."}
                            </p>
                        </div>

                        {/* Warning */}
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
                            <AlertTriangle className="h-4 w-4 text-error mt-0.5 shrink-0" />
                            <p className="text-sm text-error">
                                {labels.deleteWarning || "All your data, predictions, and activity will be permanently removed."}
                            </p>
                        </div>

                        {/* Confirmation Input */}
                        <div className="space-y-1.5">
                            <label htmlFor="confirmText" className="block text-sm font-medium text-foreground">
                                {labels.deleteConfirmationLabel || 'Type "delete" to confirm'}
                            </label>
                            <input id="confirmText" value={confirmationText} onChange={e => setConfirmationText(e.target.value)} placeholder={labels.deleteConfirmationPlaceholder || "delete"} disabled={isDeleting} className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-error/50 disabled:opacity-50" />
                        </div>

                        {error && <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
                                <AlertTriangle className="h-4 w-4 text-error mt-0.5 shrink-0" />
                                <p className="text-sm text-error">{error}</p>
                            </div>}

                        {/* Actions */}
                        <div className="flex flex-col gap-3">
                            <button onClick={handleConfirmDelete} disabled={!isConfirmationValid || isDeleting} className="w-full py-2 px-4 bg-error text-white rounded-lg font-medium text-sm hover:bg-error/90 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {isDeleting ? <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {labels.deleting || "Deleting..."}
                                    </> : <>
                                        <Trash2 className="w-4 h-4" />
                                        {labels.confirmDelete || "Permanently Delete Account"}
                                    </>}
                            </button>
                            <button onClick={handleCancel} disabled={isDeleting} className="btn-plain w-full py-2 px-4 font-medium text-sm transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                {labels.cancel || "Cancel"}
                            </button>
                        </div>
                    </div>
                </div>}
        </>;
}